import React, { useMemo, useState } from 'react';
import { PAYMENT_PROVIDERS, PaymentProvider } from '../constants/paymentProviders';

const ProviderCard: React.FC<{ provider: PaymentProvider }> = ({ provider }) => {
  return (
    <div className="border rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{provider.name}</h3>
        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">{provider.category}</span>
      </div>
      <p className="text-sm text-gray-600 mt-1">{provider.description}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <span className={`text-xs px-2 py-1 rounded ${provider.status === 'implemented' ? 'bg-green-100 text-green-800' : provider.status === 'planned' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-200 text-gray-700'}`}>
          {provider.status}
        </span>
        {provider.tags?.map((t) => (
          <span key={t} className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700">{t}</span>
        ))}
        {provider.hasFrontend && (
          <span className="text-xs px-2 py-1 rounded bg-purple-50 text-purple-700">Frontend</span>
        )}
        {provider.hasBackend && (
          <span className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700">Backend</span>
        )}
      </div>
      {(provider.links?.length || provider.docsUrl) && (
        <div className="mt-3 text-sm">
          {provider.docsUrl && (
            <a className="text-blue-600 hover:underline mr-3" href={provider.docsUrl} target="_blank" rel="noreferrer">
              Docs
            </a>
          )}
          {provider.links?.map((l) => (
            <a key={l.href} className="text-blue-600 hover:underline mr-3" href={l.href} target="_blank" rel="noreferrer">
              {l.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const PaymentProvidersPage: React.FC = () => {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PAYMENT_PROVIDERS;
    return PAYMENT_PROVIDERS.filter((p) =>
      [p.id, p.name, p.description, p.category, ...(p.tags || [])].some((v) => v.toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Payment Providers</h2>
        <div>
          <input
            className="border rounded px-3 py-2 text-sm"
            placeholder="Search providers..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((p) => (
          <ProviderCard key={p.id} provider={p} />
        ))}
      </div>

      <div className="mt-6 text-sm text-gray-600">
        Tips
        <ul className="list-disc ml-5 mt-1">
          <li>To add or modify providers, edit src/constants/paymentProviders.ts</li>
          <li>Use the provider registry in other components to avoid duplicate definitions</li>
        </ul>
      </div>
    </div>
  );
};

export default PaymentProvidersPage;
